import React, { memo, useMemo, forwardRef, useCallback, useRef, useContext, } from 'react';
import cn from 'classnames';
import { Box } from '../box';
import { Typography } from '../typography';
import { ListItemBase } from '../list-item';
import { Lead, Text, Trail } from '../slots';
import { CheckIcon, MinusMiniIcon } from '@gwc/icons';
import { usePatchedFocusRing } from '../../hooks';
import { useHover, usePress } from '@react-aria/interactions';
import { mergeProps, useId } from '@react-aria/utils';
import { useCheckboxGroupState } from '@react-stately/checkbox';
import { useCheckboxGroup, useCheckboxGroupItem } from '@react-aria/checkbox';
import styles from './styles.module.css';
export const CheckboxBase = memo(forwardRef(function CheckboxBase({ disabled, checked, size = 'm', onChange, name, hovered, error, focused, className, indeterminate, checkIcon, indeterminateIcon, labelId, tabIndex, inputProps, ...rest }, ref) {
    const inputRef = useRef(null);
    // без этого чекбоксы могут не работать в связке с другими хуками
    // например с useTableSelectionCheckbox
    const { pressProps } = usePress({ ref: inputRef });
    return (React.createElement("div", { className: cn(className, styles.checkbox, styles[size], {
            [styles.disabled]: disabled,
            [styles.hovered]: hovered,
            [styles.error]: error,
            [styles.focused]: focused,
            [styles.checked]: checked,
            [styles.indeterminate]: indeterminate,
        }), ref: ref, ...rest },
        !!indeterminate ? indeterminateIcon : !!checked ? checkIcon : undefined,
        React.createElement("input", { id: labelId, name: name, type: "checkbox", 
            // что будет при indeterminate определяется имплементацией конкретного controlled компонента
            checked: onChange ? checked : undefined, onChange: disabled ? undefined : onChange, className: styles.hiddenInput, readOnly: disabled || !onChange, disabled: disabled || !onChange, 
            // !onChange чтобы фокусились только контролд-компоненты
            tabIndex: disabled ? -1 : tabIndex, "data-indeterminate": indeterminate, ref: inputRef, ...pressProps, ...inputProps })));
}));
function useCheckbox({ onChange, ...rest }) {
    const { disabled } = rest;
    const { hoverProps, isHovered } = useHover({ isDisabled: disabled });
    const { isFocusVisible, focusProps } = usePatchedFocusRing({ within: true });
    const onChangeHandler = useCallback((e) => {
        onChange === null || onChange === void 0 ? void 0 : onChange(e.currentTarget.checked, e);
    }, [onChange]);
    return mergeProps(rest, { onChange: onChangeHandler, focused: isFocusVisible, hovered: isHovered }, hoverProps, focusProps);
}
const checkIcon = React.createElement(CheckIcon, null);
const indeterminateIcon = React.createElement(MinusMiniIcon, null);
/**
 * Простой одиночный чекбокс
 */
const CheckboxSimple = memo(forwardRef(function Checkbox(props, ref) {
    const checkboxProps = useCheckbox(props);
    return (React.createElement(CheckboxBase, { checkIcon: checkIcon, indeterminateIcon: indeterminateIcon, ref: ref, ...checkboxProps }));
}));
const CheckboxGroupContext = React.createContext(null);
/**
 * Элемент группы. Используется только в связке с Checkbox.Group
 */
export function CheckboxGroupItem({ disabled, indeterminate, ...rest }) {
    let contextValue = React.useContext(CheckboxGroupContext);
    let ref = React.useRef(null);
    if (!contextValue) {
        throw new Error('Checkbox.Item should be placed inside Checkbox.Group component');
    }
    const { state, size } = contextValue;
    let { inputProps } = useCheckboxGroupItem({ ...rest, isDisabled: disabled, isIndeterminate: indeterminate }, state, ref);
    let isDisabled = state.isDisabled || disabled;
    let isSelected = state.isSelected(rest.value);
    return (React.createElement(CheckboxSimple, { checked: isSelected, disabled: isDisabled, indeterminate: indeterminate, inputProps: inputProps, size: size, ...rest }));
}
/**
 * Обёртка для чекбоксом с лейблом
 */
export const CheckboxWithLabel = ({ align = 'left', label, labelId, checkboxProps, type, size = 'm', ...rest }) => {
    const id = useId();
    const finalLabelId = labelId || id;
    const checkbox = type === 'item' ? (React.createElement(CheckboxGroupItem, { labelId: finalLabelId, "arial-labelledby": `label_${finalLabelId}`, ...checkboxProps })) : (React.createElement(CheckboxSimple, { labelId: finalLabelId, "arial-labelledby": `label_${finalLabelId}`, size: size, ...checkboxProps }));
    return (React.createElement(ListItemBase, { ...rest, size: size },
        align === 'left' ? React.createElement(Lead, { mr: size === 'm' ? 's' : undefined }, checkbox) : null,
        React.createElement(Text, null,
            React.createElement(Typography, { Component: "label", variant: "body2", htmlFor: finalLabelId, id: `label_${finalLabelId}` }, label)),
        align !== 'left' ? (React.createElement(Trail, { pl: "s", pr: size === 'm' ? 'm' : 's' }, checkbox)) : null));
};
/**
 * Вспомогательный элемент для рендера обёртки над списком айтемов
 */
const CheckboxGroupItems = memo(({ items = [] }) => {
    const groupId = useId();
    const contextValue = useContext(CheckboxGroupContext);
    if (!contextValue) {
        throw new Error('CheckboxGroupItems should be placed inside Checkbox.Group component');
    }
    const { align, size } = contextValue;
    return (React.createElement(React.Fragment, null, items.map(({ label, ...itemRest }, i) => (React.createElement("div", { className: styles.groupItem, key: i },
        React.createElement(CheckboxWithLabel, { label: label, labelId: `${groupId}_${i}`, align: align, checkboxProps: itemRest, type: "item", size: size }))))));
});
/**
 * Группа чекбоксов связанных по смыслу. Например, фильр городов
 */
export const CheckboxGroup = memo(forwardRef(function CheckboxGroup(props, ref) {
    const { children, label, errorMessage, validationState, renderError, variant = 'default', labelProps: labelConfig, items = [], align = 'left', size = 'm', } = props;
    const ownState = useCheckboxGroupState({ ...props, isDisabled: props.disabled });
    const state = props.state || ownState;
    const { groupProps, labelProps, errorMessageProps } = useCheckboxGroup({
        ...props,
        isDisabled: props.disabled,
    }, state);
    const errorElement = useMemo(() => {
        if (validationState !== 'invalid' || !renderError || !errorMessage) {
            return null;
        }
        return renderError(errorMessageProps, errorMessage);
    }, [renderError, validationState, errorMessage]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { ...groupProps, className: cn(styles.group, styles[variant]) },
            label && (React.createElement(Typography, { Component: "div", variant: "body2", thickness: "medium", mb: size, pl: align === 'left' ? 's' : size, ...labelProps, ...labelConfig }, label)),
            React.createElement("div", { className: styles.groupContent },
                React.createElement(CheckboxGroupContext.Provider, { value: { state, align, size } },
                    React.createElement(React.Fragment, null,
                        items.length ? React.createElement(CheckboxGroupItems, { items: items }) : null,
                        children)))),
        errorElement));
}));
export const Checkbox = Object.assign(CheckboxSimple, {
    Item: CheckboxGroupItem,
});
